import styled from "styled-components";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import CheckBox from "components/atoms/CheckBox";
import { COLOR_SYSTEM } from "components/design/design-system";
import { typography } from "components/design/typography";
import { useState } from "react";

const PageContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[0]};
`;

const Container = styled.div`
  width: 60%;
  margin: 40px 0;
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const Content = styled.div`
  padding: 40px;
`;

const BlueBar = styled.div`
  width: 4px;
  height: 24px;
  background-color: ${COLOR_SYSTEM.get("Blue")?.[500]};
  margin-right: 12px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;

const Description = styled.div`
  margin-bottom: 40px;
`;

const HighlightText = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const NoticeBox = styled.div`
  padding: 32px;
  border-left: 4px solid ${COLOR_SYSTEM.get("Blue")?.[500]};
`;

const NoticeTitle = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const NoticeDescription = styled.div`
  margin-bottom: 24px;
`;

const NoticeSection = styled.div`
  margin-bottom: 24px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const NoticeList = styled.div`
  padding-left: 20px;
  display: flex;
  flex-direction: column;
`;

const WarningBox = styled.div`
  margin-top: 24px;
  padding: 16px;
  background-color: ${COLOR_SYSTEM.get("Red")?.[50]};
  border-radius: 4px;
`;

const CheckboxContainer = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;
`;

interface GuideProps {
  onAgree: () => void;
  onDisagree: () => void;
}

const Guide: React.FC<GuideProps> = ({ onAgree, onDisagree }) => {
  const [isChecked, setIsChecked] = useState(false);
  const [isAIChecked, setIsAIChecked] = useState(false);

  const onCheckChange = () => {
    setIsChecked(!isChecked);
  };

  const onAICheckChange = () => {
    setIsAIChecked(!isAIChecked);
  };

  return (
    <PageContainer>
      <Container>
        <Content>
          <TitleContainer>
            <Text textType="H2" color={COLOR_SYSTEM.get("Gray")?.[900]}>
              안녕하세요, 약사님😊
            </Text>
          </TitleContainer>

          <Description>
            <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
              PhAI는 인공지능으로 약사의 의사결정을 보조하는 시스템입니다.
            </Text>
            <HighlightText>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Yellow")?.[500]}
                style={{ fontWeight: 600 }}
              >
                반복적인 자료 검토는 AI가,
              </Text>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Blue")?.[500]}
                style={{ fontWeight: 600 }}
              >
                약사님은 임상적 판단과 환자상담을.
              </Text>
            </HighlightText>
            <Text
              textType="Body1"
              color={COLOR_SYSTEM.get("Gray")?.[700]}
              style={{ marginTop: 16, fontWeight: 600 }}
            >
              AI와 협업으로 약사 본연의 가치에 더 집중하세요.
            </Text>
          </Description>

          <NoticeBox>
            <NoticeTitle>
              <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
                1. 개인정보 입력 시 주의사항
              </Text>
            </NoticeTitle>

            <NoticeDescription>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Gray")?.[700]}
                style={{ fontWeight: 600 }}
              >
                본 서비스는 개인정보보호법 준수를 위해 개인식별정보 없이 분석을 수행합니다. 다음 정보는 절대 입력하지 마십시오:
              </Text>
            </NoticeDescription>

            <NoticeSection>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Gray")?.[900]}
                style={{ fontWeight: 600, marginBottom: 8 }}
              >
                ❌ 입력 금지 정보
              </Text>
              <NoticeList>
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  환자 성명, 생년월일, 주민등록번호
                </Text>
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  연락처, 주소, 이메일, 의료기록번호, 보험번호, 
                </Text>
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  기타 개인을 특정할 수 있는 모든 정보
                </Text>
              </NoticeList>
            </NoticeSection>

            <NoticeSection>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Gray")?.[900]}
                style={{ fontWeight: 600, marginBottom: 8 }}
              >
                ✅ 입력 가능 정보
              </Text>
              <NoticeList>
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  질병명 및 진단코드
                </Text>
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  처방 약물명 및 용량
                </Text>
                <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  성별, 연령대 (예: 70대초반)
                </Text>
              </NoticeList>
            </NoticeSection>

            <WarningBox>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                ※ 개인식별정보 입력으로 개인정보보호법 위반 시, 법적 책임이 발생할 수 있습니다.
              </Text>
            </WarningBox>

            <CheckboxContainer onClick={onCheckChange} style={{ cursor: 'pointer' }}>
              <CheckBox checked={isChecked} onClick={onCheckChange} size="Large" />
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                위 내용을 이해했으며, 안내사항을 준수하겠습니다.
              </Text>
            </CheckboxContainer>
          </NoticeBox>

          <div style={{ marginBottom: '40px' }} />

          <NoticeBox>
            <NoticeTitle>
              <Text textType="H3" color={COLOR_SYSTEM.get("Blue")?.[500]}>
                2. AI 분석 결과 확인 시 주의사항
              </Text>
            </NoticeTitle>

            <NoticeDescription>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Gray")?.[700]}
                style={{ fontWeight: 600 }}
              >
                AI는 실수할 수 있습니다. 약사의 검증이 필수입니다.
              </Text>
            </NoticeDescription>

            <NoticeSection>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Orange")?.[600]}
                style={{ fontWeight: 600, marginBottom: 8 }}
              >
                ⚠️ AI 분석의 한계
              </Text>
              <NoticeList>
                <Text element="div" textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  AI는 틀린 정보를 제공하거나 근거 자료를 잘못 해석할 수 있습니다
                </Text>
                <Text element="div" textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  복잡한 임상 상황에서는 AI가 놓치는 요소가 있을 수 있습니다
                </Text>
              </NoticeList>
            </NoticeSection>

            <NoticeSection>
              <Text
                textType="Body1"
                color={COLOR_SYSTEM.get("Blue")?.[600]}
                style={{ fontWeight: 600, marginBottom: 8 }}
              >
                ✅ 약사의 전문적 책임
              </Text>
              <NoticeList>
                <Text element="div" textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  AI가 제시한 근거 자료를 반드시 직접 확인하세요
                </Text>
                <Text element="div" textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  근거가 명시되지 않은 내용은 전문 지식으로 추가 검증하세요
                </Text>
              </NoticeList>
            </NoticeSection>

            <WarningBox>
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                ※ 모든 처방 검토 및 상담 결과에 대한 최종 판단과 책임은 약사에게 있습니다
              </Text>
            </WarningBox>

            <CheckboxContainer onClick={onAICheckChange} style={{ cursor: 'pointer' }}>
              <CheckBox checked={isAIChecked} onClick={onAICheckChange} size="Large" />
              <Text textType="Body1" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                위 내용을 이해했으며, AI 분석 결과를 참고용으로만 활용하겠습니다.
              </Text>
            </CheckboxContainer>
          </NoticeBox>

          <ButtonContainer>
            <Button
              type="Text"
              variant="Contained"
              color="Primary"
              size="Large"
              onClick={onAgree}
              disabled={!isChecked || !isAIChecked}
              disabledColor={!isChecked || !isAIChecked}
              style={{ flex: 1 }}
            >
              동의하고 서비스 사용하기
            </Button>
            <Button
              type="Text"
              variant="Contained"
              color="Secondary"
              size="Large"
              onClick={onDisagree}
              style={{ flex: 1 }}
            >
              동의 안함 (로그아웃)
            </Button>
          </ButtonContainer>
        </Content>
      </Container>
    </PageContainer>
  );
};

export default Guide;

